import React, { useMemo, useRef, useState } from 'react';

import TopBar from '../components/TopBar';
import { useDialog } from '../components/Dialog';
import Mp3ExportModal from '../components/Mp3ExportModal';
import { useStore } from '../store';
import { canExportMp3 } from '../mp3';
import { PdfError, extractPdfText, isPdfFile, type PdfProgress } from '../pdf';
import { DEFAULT_VOICE, SPEED_RATES } from '../tts';
import { csvToLines, parseSentenceLines, splitSpeaker } from '../utils';

const NEW_UNIT = '__new__';

const PDF_ERROR_KEY: Record<string, string> = {
  password: 'pdf.err_password',
  corrupt: 'pdf.err_corrupt',
  no_text: 'pdf.err_no_text',
};

/** Reduce a name to something safe for a download attribute. */
function safeBaseName(name: string): string {
  const cleaned = name.replace(/[^\w.\- ]+/g, '').trim();
  return cleaned || 'speak2remember';
}

export default function AddSentencesScreen() {
  const s = useStore();
  const dialog = useDialog();
  const [unitChoice, setUnitChoice] = useState<string>(NEW_UNIT); // NEW_UNIT or String(sid)
  const [partName, setPartName] = useState('');
  const [unitName, setUnitName] = useState(`Unit ${s.nextSid()}`);
  const [voiceKey, setVoiceKey] = useState<string>(DEFAULT_VOICE);
  const [persona, setPersona] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [pdfProgress, setPdfProgress] = useState<PdfProgress | null>(null);
  const [exporting, setExporting] = useState(false);
  const [sourceName, setSourceName] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const mp3Supported = useMemo(() => canExportMp3(), []);

  const targetLesson = useMemo(
    () => (unitChoice === NEW_UNIT ? null : s.lessons.find((l) => String(l.sid) === unitChoice) ?? null),
    [unitChoice, s.lessons],
  );

  // The RN build used expo-document-picker; on the web a hidden file input is
  // the whole story — no permission prompt, no native module.
  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    try {
      let content: string;
      if (isPdfFile(file)) {
        // A PDF is parsed rather than read (see src/pdf.ts) and a long one
        // takes a few seconds, so the button reports the page it is on.
        setPdfProgress({ page: 0, pages: 0 });
        try {
          content = await extractPdfText(file, setPdfProgress);
        } finally {
          setPdfProgress(null);
        }
      } else {
        content = await file.text();
        if (file.name.toLowerCase().endsWith('.csv') || file.type.includes('csv')) {
          content = csvToLines(content);
        }
      }
      setSourceName(file.name.replace(/\.[^.]+$/, '')); // the MP3 is named after it
      setText((prev) => (prev.trim() ? `${prev}\n${content.trim()}\n` : `${content.trim()}\n`));
    } catch (err) {
      console.error('[add] import failed', err);
      const key = err instanceof PdfError ? PDF_ERROR_KEY[err.code] : 'error.file_read_failed';
      await dialog.alert(s.t('error.title'), s.t(key), s.t('action.close'));
    }
  };

  // What the MP3 export reads out: the English side only, since the Korean
  // column is a written gloss. Persona mode hides the "NAME:" prefix when
  // speaking, so it is dropped here too — the file matches what the study
  // screens would say.
  const mp3Lines = useMemo(
    () => parseSentenceLines(text).map((p) => (persona ? splitSpeaker(p.eng)[1] : p.eng)),
    [text, persona],
  );

  const startMp3Export = async () => {
    if (!mp3Lines.length) {
      await dialog.alert(
        s.t('dialog.no_sentences_title'),
        s.t('dialog.no_sentences_body'),
        s.t('action.close'),
      );
      return;
    }
    setExporting(true);
  };

  const save = async () => {
    if (saving) return;
    const pairs = parseSentenceLines(text);
    if (!pairs.length) {
      await dialog.alert(
        s.t('dialog.no_sentences_title'),
        s.t('dialog.no_sentences_body'),
        s.t('action.close'),
      );
      return;
    }
    setSaving(true);
    try {
      const { ok } = await s.addSentences(targetLesson, partName, unitName, pairs, voiceKey, persona);
      await dialog.alert(
        s.t('dialog.success_title'),
        s.t('dialog.success_body', { ok }),
        s.t('action.close'),
      );
      s.backToUnitList();
    } catch (e) {
      console.error('[add] save failed', e);
      await dialog.alert(s.t('error.title'), String(e), s.t('action.close'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="screen">
      <TopBar
        title={s.t('screen.add_sentences.title')}
        subtitle={s.t('screen.add_sentences.subtitle')}
        onBack={s.backToUnitList}
        backLabel={s.t('nav.back')}
      />
      <div className="scroll">
        <div className="body">
          {/* ---- unit selection ---- */}
          <span className="form-label">{s.t('add.unit_select_label')}</span>
          <div className="chips">
            <Chip
              text={s.t('unit.new_label')}
              active={unitChoice === NEW_UNIT}
              onSelect={() => setUnitChoice(NEW_UNIT)}
            />
            {s.lessons.map((l) => (
              <Chip
                key={l.sid}
                text={l.unit.trim() || `Unit ${l.sid}`}
                active={unitChoice === String(l.sid)}
                onSelect={() => setUnitChoice(String(l.sid))}
              />
            ))}
          </div>

          {unitChoice === NEW_UNIT && (
            <div className="new-unit-row">
              <div>
                <label className="small-label" htmlFor="part-name">
                  {s.t('add.part_name_label')}
                </label>
                <input
                  id="part-name"
                  className="input"
                  value={partName}
                  onChange={(e) => setPartName(e.target.value)}
                />
              </div>
              <div>
                <label className="small-label" htmlFor="unit-name">
                  {s.t('add.unit_name_label')}
                </label>
                <input
                  id="unit-name"
                  className="input"
                  value={unitName}
                  onChange={(e) => setUnitName(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* ---- voice ---- */}
          <span className="form-label form-label--mt">{s.t('add.voice_label')}</span>
          <div className="chips">
            {s.voiceOptions.map((v) => (
              <Chip
                key={v.key}
                text={v.label}
                active={voiceKey === v.key}
                onSelect={() => setVoiceKey(v.key)}
              />
            ))}
          </div>
          <p className="hint">{s.t('add.voice_hint')}</p>

          {/* ---- persona ---- */}
          <button
            type="button"
            className="check-row"
            onClick={() => setPersona((p) => !p)}
            role="switch"
            aria-checked={persona}
            style={{ marginTop: '0.9rem' }}
          >
            <span className={`checkbox${persona ? ' is-on' : ''}`} aria-hidden="true">
              ✓
            </span>
            <span className="check-row__label" style={{ fontWeight: 700 }}>
              {s.t('add.persona_checkbox')}
            </span>
          </button>
          <p className="hint">{s.t('add.persona_hint')}</p>

          {/* ---- text input ---- */}
          <label className="form-label form-label--mt" htmlFor="sentences">
            {s.t('add.text_input_label')}
          </label>
          <p className="example">{s.t('add.text_input_example1')}</p>
          <p className="example">{s.t('add.text_input_example2')}</p>
          <textarea
            id="sentences"
            className="textarea textarea--tall"
            style={{ marginTop: '0.4rem' }}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Nice to meet you. | 만나서 반갑습니다."
            spellCheck={false}
          />

          <div className="btn-row btn-row--wrap">
            <button
              type="button"
              className="btn btn--outline"
              onClick={() => fileRef.current?.click()}
              disabled={!!pdfProgress}
            >
              {/* The page count is unknown until the document opens. */}
              {!pdfProgress
                ? s.t('add.import_file_button')
                : pdfProgress.pages
                  ? s.t('add.import_pdf_progress', {
                      page: pdfProgress.page,
                      pages: pdfProgress.pages,
                    })
                  : s.t('add.import_pdf_opening')}
            </button>
            <button
              type="button"
              className="btn btn--outline"
              onClick={startMp3Export}
              disabled={!mp3Supported}
              title={mp3Supported ? undefined : s.t('mp3.err_unsupported')}
            >
              {s.t('mp3.export_button')}
            </button>
            <button type="button" className="btn btn--outline" onClick={() => setText('')}>
              {s.t('action.clear')}
            </button>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".txt,.csv,.pdf,text/plain,text/csv,application/pdf"
            className="visually-hidden"
            onChange={onPickFile}
          />

          <button
            type="button"
            className="btn btn--primary btn--block"
            style={{ marginTop: '1.1rem' }}
            onClick={save}
            disabled={saving}
          >
            {s.t('add.save_button')}
          </button>
        </div>
      </div>

      {exporting && (
        <Mp3ExportModal
          lines={mp3Lines}
          baseName={safeBaseName(sourceName || unitName)}
          rate={SPEED_RATES[s.progress.repeat_speed]}
          voiceId={s.voiceOptions.find((o) => o.key === voiceKey)?.identifier}
          onClose={() => setExporting(false)}
        />
      )}
    </div>
  );
}

function Chip({ text, active, onSelect }: { text: string; active: boolean; onSelect: () => void }) {
  return (
    <button type="button" className="chip" aria-pressed={active} onClick={onSelect}>
      {text}
    </button>
  );
}
